import _ from 'lodash';
import { reactive, Ref, ref, onUnmounted, computed, ComputedRef } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { loopAsyncTask } from '@/renderer/scripts/utils/syncTask';
import { formatError } from '@/renderer/scripts/utils/errorCode/index';
type targetPath = {
  value: string;
};
type FailInfo = {
  list: unknown[];
  type: string;
};
type CopyRequest = {
  copyFrom: string;
  to: string;
  id?: number;
  conflictResolutionStrategy?: string;
  type?: string;
};
type CopyParam = {
  path: string;
  orgSpaceId: string;
  dstSpaceId: string;
  fileList: SMH.SpaceFileItemData[];
};
type Handler = {
  show: Ref<boolean>;
  methods: {
    openSelectFolder: (item: SMH.SpaceFileItemData[]) => void;
    onCopy: (path: targetPath) => void;
    onHotKeyCopy: (copyParam: CopyParam) => void;
  };
};
export default function handler(spaceId: Ref<SpaceId>): Handler {
  const show = ref(false);
  const failInfo = reactive<FailInfo>({
    list: [],
    type: 'copy',
  });
  let originFileList: SMH.SpaceFileItemData[] = [];
  let pathKeep: targetPath;
  let spaceIdKeep: string;
  let isHotKeyCopy = false;
  const methods = {
    openSelectFolder(item: SMH.SpaceFileItemData[]) {
      show.value = true;
      originFileList = _.cloneDeep(item);
    },
    async onCopy(path: targetPath, conflictResolutionStrategy = 'ask') {
      tips.loading(i18n_t('正在复制'), 0);
      pathKeep = path;
      isHotKeyCopy = false;
      await methods.copyImpl(
        {
          path: path.value,
          orgSpaceId: spaceId.value,
          dstSpaceId: '',
          fileList: originFileList,
        },
        conflictResolutionStrategy,
      );
    },
    async onHotKeyCopy(copyParam: CopyParam, conflictResolutionStrategy = 'ask') {
      tips.loading(i18n_t('正在复制'), 0);
      // 这里保存一下数据用于重试
      pathKeep = {
        value: copyParam.path,
      };
      spaceIdKeep = copyParam.orgSpaceId;
      originFileList = _.cloneDeep(copyParam.fileList);
      isHotKeyCopy = true;
      await methods.copyImpl(copyParam, conflictResolutionStrategy);
    },
    async copyImpl(copyParam: CopyParam, conflictResolutionStrategy: string) {
      const currentPath = (copyParam.fileList[0].path as string[]).slice(0, -1).join('/');
      if (copyParam.fileList.length > 1) {
        // 批量协议
        const requestBody: Array<CopyRequest> = [];
        copyParam.fileList.forEach((item: SMH.SpaceFileItemData) => {
          requestBody.push({
            copyFrom: [currentPath, item.name].filter(s => s).join('/'),
            // 过滤空值
            to: copyParam.path ? `${copyParam.path}/${item.name}` : `${item.name}`,
            conflictResolutionStrategy: 'rename',
            type: item.type,
          });
        });
        await methods.batchCopyMethods(requestBody, copyParam.orgSpaceId);
      } else {
        // 单个协议
        const originFolderName = [currentPath, copyParam.fileList[0].name].filter(s => s).join('/');
        const targetPath = [copyParam.path, copyParam.fileList[0].name].filter(s => s).join('/');
        try {
          let res;
          if (copyParam.fileList[0].type === 'dir') {
            res = await smh.commonAPI.copyDirectory({
              dirPath: targetPath,
              copyFrom: originFolderName,
              conflictResolutionStrategy,
              spaceId: copyParam.orgSpaceId,
            });
          } else {
            res = await smh.commonAPI.copyFile({
              filePath: targetPath,
              copyFrom: originFolderName,
              conflictResolutionStrategy,
              spaceId: copyParam.orgSpaceId,
            });
          }
          if (res.status === 202) {
            const asyncRes = await loopAsyncTask(
              smh.commonAPI.asyncTaskQuery,
              res.data.taskId as number,
              copyParam.orgSpaceId,
            );
            if (asyncRes.status === 400) {
              ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
              tips.error(i18n_t('复制失败：空间不足，请清理个人空间或联系管理员扩容！'));
              return;
            }
            res.data = asyncRes.result;
          }
          // 覆盖除外
          if (
            originFolderName === targetPath &&
            copyParam.fileList[0].name !== res.data.path[res.data.path.length - 1]
          ) {
            copyParam.fileList[0].name = res.data.path[res.data.path.length - 1];
            copyParam.fileList[0].path = res.data.path; // 更新正确的path
            copyParam.fileList[0].id = Date.now();
            ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH, copyParam.fileList, 'copy');
          } else if (isHotKeyCopy) {
            ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
          }
          show.value = false;
          tips.success(i18n_t('复制成功'));
        } catch (error: any) {
          if (error.status === 409) {
            show.value = false;
            ipc.emit(ipc.RENDERER_EVENTS_MAP.CONFLICT_SHOW, copyParam.fileList[0].type, 'copy');
          } else if (error.toString() === 'Error: [SMH Warning]: Logout') {
            tips.error(i18n_t('登出导致复制失败，请登录后重试'));
          } else {
            tips.error(error as MsgError);
          }
        }
      }
    },
    async batchCopyMethods(requestBody: Array<CopyRequest>, orgSpaceId: string) {
      try {
        const res = await smh.commonAPI.batchCopy(
          {
            spaceId: orgSpaceId,
          },
          {
            body: requestBody,
          },
        );
        show.value = false;
        if (res.status === 200) {
          methods.handleBatchSuccessResult(requestBody, res.data.result);
        } else if (res.status === 202) {
          const asyncRes = await loopAsyncTask(smh.commonAPI.asyncTaskQuery, res.data.taskId, orgSpaceId);
          if (asyncRes.status === 200) {
            methods.handleBatchSuccessResult(requestBody, asyncRes.result);
          } else if (asyncRes.status === 207) {
            methods.handleBatchFailResult(requestBody, asyncRes.result);
          }
        } else {
          methods.handleBatchFailResult(requestBody, res.data.result);
        }
      } catch (error: any) {
        if (error.toString() === 'Error: [SMH Warning]: Logout') {
          tips.error(i18n_t('登出导致复制失败，请登录后重试'));
        } else {
          tips.error(error as MsgError);
        }
      }
    },
    handleBatchSuccessResult(requestBody: Array<CopyRequest>, response: Array<any>) {
      tips.success(i18n_t('复制成功'));
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ERR_LIST_CLEAR);
      const resData = response;
      if (requestBody[0].copyFrom === requestBody[0].to) {
        for (let index = 0; index < originFileList.length; index++) {
          const element = originFileList[index];
          element.name = resData[index].path[resData[index].path.length - 1];
          element.path = resData[index].path;
          element.id = Date.now() + index;
        }
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH, originFileList);
      } else if (isHotKeyCopy) {
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
      }
    },
    handleBatchFailResult(requestBody: Array<CopyRequest>, response: Array<any>) {
      failInfo.list = [];
      const data = response;
      if (requestBody[0].copyFrom === requestBody[0].to) {
        const copyAppendData: SMH.SpaceFileItemData[] = [];
        for (let index = 0; index < data.length; index++) {
          const element = data[index];
          if (element.status === 200) {
            originFileList[index].name = element.path[element.path.length - 1];
            originFileList[index].path = element.path;
            originFileList[index].id = Date.now() + index;
            copyAppendData.push(originFileList[index]);
          }
        }
        if (copyAppendData.length) {
          ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH, copyAppendData);
        }
      } else if (isHotKeyCopy) {
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
      }
      data.forEach((item: any, index: number) => {
        if (item.status !== 200 && item.status !== 204) {
          failInfo.list.push({
            copyFrom: item.copyFrom.join('/'),
            to: item.to.join('/'),
            msg: `${formatError(item.code, true)}`,
            id: index,
            type: requestBody[index].type,
          });
        }
      });
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ERRINFO_SHOW, failInfo);
    },
    async onCopyRetry(data: Array<CopyRequest>) {
      if (data.length === 1) {
        try {
          if (data[0].type === 'dir') {
            await smh.commonAPI.copyDirectory({
              dirPath: data[0].to,
              copyFrom: data[0].copyFrom,
              spaceId: spaceId.value,
            });
          } else {
            await smh.commonAPI.copyFile({
              filePath: data[0].to,
              copyFrom: data[0].copyFrom,
              spaceId: spaceId.value,
            });
          }
          tips.success(i18n_t('复制成功'));
          ipc.emit(ipc.RENDERER_EVENTS_MAP.ERR_LIST_DEL, data[0].id);
        } catch (error: any) {
          if (error.toString() === 'Error: [SMH Warning]: Logout') {
            tips.error(i18n_t('登出导致复制失败，请登录后重试'));
          } else {
            tips.error(error as MsgError);
          }
          tips.error(error as MsgError);
        }
      } else {
        await methods.batchCopyMethods(data, spaceId.value);
      }
    },
  };
  const conflictCopy = (way: string) => {
    if (isHotKeyCopy) {
      methods.onHotKeyCopy(
        {
          path: pathKeep.value,
          orgSpaceId: spaceId.value,
          dstSpaceId: '',
          fileList: originFileList,
        },
        way,
      );
    } else {
      methods.onCopy(pathKeep, way);
    }
  };
  const retryCopy = (data: Array<CopyRequest>) => {
    methods.onCopyRetry(data);
  };
  ipc.on(ipc.RENDERER_EVENTS_MAP.CONFLICT_COPY, conflictCopy);
  ipc.on(ipc.RENDERER_EVENTS_MAP.RETRY_COPY, retryCopy);
  onUnmounted(() => {
    ipc.off(ipc.RENDERER_EVENTS_MAP.CONFLICT_COPY, conflictCopy);
    ipc.off(ipc.RENDERER_EVENTS_MAP.RETRY_COPY, retryCopy);
  });
  return {
    show,
    methods,
  };
}
